import React, { Component } from "react";
import { dataFeedObject } from "./datafeed";

/** TradingView UMD must load as a classic script (see index.html), not Vite ESM. */
const getTvWidgetCtor = () => {
  const Widget = window.TradingView?.widget;
  if (typeof Widget !== "function") {
    throw new Error(
      "TradingView charting library is not loaded (window.TradingView.widget missing)"
    );
  }
  return Widget;
};

const toTvTheme = (theme) =>
  theme === "light" ? "Light" : "Dark";

const getChartOverrides = (theme) => {
  const isBlack = theme === "black";
  const isDark = theme !== "light";
  return {
    "mainSeriesProperties.showCountdown": true,
    "paneProperties.background": isBlack ? "#000000" : isDark ? "#0f1521" : "#ffffff",
    "paneProperties.vertGridProperties.color": isDark
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(15, 23, 42, 0.06)",
    "paneProperties.horzGridProperties.color": isDark
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(15, 23, 42, 0.06)",
    "symbolWatermarkProperties.transparency": 50,
    "scalesProperties.textColor": isDark ? "#9aa8bf" : "#475266",
    "scalesProperties.bgColor": "#AAA",
    "scalesProperties.fontSize": 11,
    "paneProperties.topMargin": 15,
    "mainSeriesProperties.candleStyle.upColor": "#1fc77f",
    "mainSeriesProperties.candleStyle.downColor": "#f0524d",
    "mainSeriesProperties.candleStyle.wickUpColor": "#1fc77f",
    "mainSeriesProperties.candleStyle.wickDownColor": "#f0524d",
    "mainSeriesProperties.candleStyle.borderColor": "#1fc77f",
    "mainSeriesProperties.candleStyle.borderUpColor": "#1fc77f",
    "mainSeriesProperties.candleStyle.borderDownColor": "#f0524d",
    "mainSeriesProperties.barStyle.upColor": "#1fc77f",
    "mainSeriesProperties.barStyle.downColor": "#f0524d",
    "mainSeriesProperties.barStyle.dontDrawOpen": true,
  };
};

const getLoadingScreen = (theme) => ({
  backgroundColor:
    theme === "black" ? "#000000" : theme === "light" ? "#eef1f6" : "#0f1521",
});

class TradingChartComponent extends Component {
    constructor() {
        // alert("hi")
        super(...arguments);
        this.currentKlineSubscription = {};
        this.state = {
            theme: this.props.theme,
        };

        this.params = {
            container_id: "tv_chart_container",
            library_path: "/charting_library/",
            charts_storage_url: "https://saveload.tradingview.com",
            charts_storage_api_version: "1.1",
            client_id: "tradingview.com",
            user_id: "public_user_id",
            fullscreen: false,
            autosize: true,
            locale: "en",
            disabled_features: [
                // "volume_force_overlay",
                "use_localstorage_for_settings",
                "header_symbol_search",
                "header_compare",
                "header_saveload",
                "display_market_status",
            ],
            enabled_features: [
                "show_animated_logo",
                "hide_left_toolbar_by_default",
                "timezone",
            ],
            popup_with: "000",
            enable_publishing: false,
            withdateranges: false,
            hide_side_toolbar: true,
            allow_symbol_change: true,
            details: true,
            hotlist: true,
            calendar: true,
            show_popup_button: true,
            popup_height: "100",
            overrides: getChartOverrides(this.state.theme),
            loading_screen: getLoadingScreen(this.state.theme),
            custom_css_url: `/charting_library/static/css/header.css`,
        };

        this.tvWidget = null;

        this.datafeed = dataFeedObject(
            this.props.pricescaleValue,
            this.props.priceDecimal,
            this.props.baseDecimal,
            this.props.selectedPairKey
        );

        this.setChart = (selectedPairKey) => {
            this.datafeed = dataFeedObject(
                this.props.pricescaleValue,
                this.props.priceDecimal,
                this.props.baseDecimal,
                selectedPairKey
            );
            const widgetOptions = {
                interval: this.props.interval,
                symbol: this.props.symbol,
                theme: toTvTheme(this.props.theme),
                datafeed: this.datafeed,
                ...this.params,
            };
            const Widget = getTvWidgetCtor();
            this.tvWidget = new Widget(widgetOptions);
            this.tvWidget.onChartReady(() => {
                try {
                    this.tvWidget.applyOverrides(getChartOverrides(this.props.theme));
                } catch {
                    /* ignore */
                }
            });
        };

        this.applyChartTheme = (theme) => {
            if (!this.tvWidget) return;
            const overrides = getChartOverrides(theme);
            this.params.overrides = overrides;
            this.params.loading_screen = getLoadingScreen(theme);
            const tvTheme = toTvTheme(theme);

            const applyOverrides = () => {
                if (!this.tvWidget) return;
                try {
                    this.tvWidget.applyOverrides(overrides);
                } catch {
                    /* ignore override failures during theme swap */
                }
            };

            try {
                // Toolbar/footer chrome needs changeTheme("Dark"|"Light").
                this.tvWidget.changeTheme(tvTheme);
            } catch {
                /* older chart builds may not support changeTheme */
            }

            // Re-apply pane colors after chrome theme settles.
            try {
                this.tvWidget.onChartReady(applyOverrides);
            } catch {
                /* ignore */
            }
            window.setTimeout(applyOverrides, 0);
            window.setTimeout(applyOverrides, 120);
        };

        this.updateChart = (selectedPairKey, priceDecimal, baseDecimal, theme) => {
            if (this.tvWidget) {
                try {
                    this.tvWidget.remove();
                } catch {
                    /* ignore cleanup failures before recreate */
                }
            }
            this.tvWidget = null;
            this.datafeed = dataFeedObject(
                this.props.pricescaleValue,
                priceDecimal,
                baseDecimal,
                selectedPairKey
            );
            this.params.overrides = getChartOverrides(theme);
            this.params.loading_screen = getLoadingScreen(theme);
            const widgetOptions = {
                interval: this.props.interval,
                symbol: this.props.symbol,
                theme: toTvTheme(theme),
                datafeed: this.datafeed,
                ...this.params,
            };
            const Widget = getTvWidgetCtor();
            this.tvWidget = new Widget(widgetOptions);
            if (this.tvWidget) {
                this.tvWidget.onChartReady(() => {
                    try {
                        this.tvWidget.applyOverrides(getChartOverrides(theme));
                    } catch {
                        /* ignore */
                    }
                });
            }
        };
    }

    componentDidUpdate(prevProps) {
        const pairChanged =
            this.props.selectedPairKey?.pair_key &&
            (!prevProps.selectedPairKey?.pair_key ||
                this.props.selectedPairKey.pair_key !== prevProps.selectedPairKey.pair_key);
        const themeChanged = this.props.theme !== prevProps.theme;

        if (pairChanged) {
            this.setState({ theme: this.props.theme });
            if (prevProps.selectedPairKey?.pair_key && this.tvWidget) {
                this.updateChart(
                    this.props.selectedPairKey,
                    this.props.priceDecimal,
                    this.props.baseDecimal,
                    this.props.theme
                );
            } else {
                this.setChart(this.props.selectedPairKey);
            }
        } else if (themeChanged) {
            this.setState({ theme: this.props.theme });
            this.applyChartTheme(this.props.theme);
        }

        if (
            Object.keys(this.props.kline || {}).length > 0 &&
            this.props.kline !== prevProps.kline
        ) {
            this.datafeed.onRealtimeCallback(this.props.kline);
        }
    }

    componentDidMount() {
        if (this.props.selectedPairKey) {
            this.setState({ theme: this.props.theme })
            this.setChart(this.props.selectedPairKey);
        }
    }

    componentWillUnmount() {
        if (this.tvWidget) {
            try {
                this.tvWidget.remove();
            } catch (error) {
                // console.log(`TradingChart unmount failed: ${error}`);
            }
        }
    }

    render() {
        return (
            <>
                <div id={this.params.container_id} />
            </>
        );
    }
}

export default TradingChartComponent;
